import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import { gridGapStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import breakpointMediaQueries, { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface SideMenuStyles {
    container?: InjectableCss;
    grid?: GridContainerProps;
    signInGridItem?: GridItemProps;
    separatorGridItem?: GridItemProps;
    separator?: TypographyProps;
    createGridItem?: GridItemProps;
    spacerGridItem?: GridItemProps;
}

type Props = ReturnType<typeof mapStateToProps> & WidgetProps;

const mapStateToProps = (state: ApplicationState) => {
    const settings = getStoreSettingsCollection(state);

    return {
        allowInternalSignIn: settings.poolcorpSettings.allowInternalSignIn,
        allowCreateAccount: settings.accountSettings.allowCreateAccount,
    };
};

const styles = {
    container: {},
    grid: {
        gap: 0,
        css: css`
            ${gridGapStyles([], [TYPOGRAPHY.spacing.large])}
        `,
    },
    signInGridItem: {
        width: [12, 12, 5, 4, 4] as GridWidths[],
    },
    separatorGridItem: {
        width: [12, 12, 2, 2, 2] as GridWidths[],
        css: css`
            position: relative;

            &::before {
                content: "";
                position: absolute;
                top: 50%;
                right: 0;
                left: 0;
                height: 1px;
                background-color: ${getColor("common.border")};

                ${props =>
                    breakpointMediaQueries(
                        props.theme,
                        [
                            null,
                            null,
                            css`
                                top: 0;
                                right: auto;
                                bottom: 0;
                                left: 50%;
                                width: 1px;
                                height: auto;
                            `,
                        ],
                        mediaOptions.min,
                    )}
            }
        `,
    },
    separator: {
        css: css`
            position: relative;
            margin: auto;
            background-color: ${getColor("common.background")};
            padding: 0 1em;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.smaller};

            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        null,
                        css`
                            padding: 1em 0;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    createGridItem: {
        width: [12, 12, 5, 5, 4] as GridWidths[],
    },
    spacerGridItem: {
        width: [0, 0, 0, 1, 2] as GridWidths[],
    },
};
export const signInStyles = styles;

const SignInView: React.FunctionComponent<Props> = ({ id, allowInternalSignIn, allowCreateAccount }) => {
    return (
        <StyledWrapper {...styles.container}>
            <GridContainer {...styles.grid}>
                <GridItem {...styles.signInGridItem}>
                    <Zone contentId={id} zoneName="Left" />
                </GridItem>
                {allowCreateAccount ? (
                    <GridItem {...styles.separatorGridItem}>
                        <Typography {...styles.separator}>{translate("OR")}</Typography>
                    </GridItem>
                ) : (
                    <GridItem width={styles.separatorGridItem.width}></GridItem>
                )}
                <GridItem {...styles.createGridItem}>
                    <Zone contentId={id} zoneName="Right" />
                </GridItem>
                <GridItem {...styles.spacerGridItem} />
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(SignInView),
    definition: {
        allowedContexts: ["SignInPage"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [],
    },
};

export default widgetModule;
